import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import AccountCircle from '@mui/icons-material/AccountCircle'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import SettingsIcon from '@mui/icons-material/Settings'
import MuiAppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import { useMutation } from '@tanstack/react-query'
import Cookies from 'js-cookie'
import { changeLoginState } from '~/oakra/actions/login'
import LanguageButton from '~/oakra/components/language-button'
import { useAppSelector } from '~/store'
import { getPath } from '~/utils'

function useLogoutMutation() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const logoutAPIURL = useAppSelector((state) => state.initial.endpoints.logoutAPIURL)

    return useMutation({
        mutationFn: async () => {
            return await fetch(logoutAPIURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'X-CSRFToken': Cookies.get('csrftoken') ?? '',
                },
            })
        },
        onSuccess: () => {
            dispatch(changeLoginState(false))
            navigate('/login')
        },
    })
}

export default function AppBar() {
    const staticRoot = useAppSelector((state) => state.initial.staticRoot)
    const loggedIn = useAppSelector((state) => state.login.loggedIn)
    const navigate = useNavigate()

    const [appBarMenuEl, setAppBarMenuEl] = useState<HTMLElement | null>(null)
    const open = !!appBarMenuEl

    const { mutate } = useLogoutMutation()

    return (
        <MuiAppBar
            position="fixed"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar variant="dense">
                <Link
                    href="/"
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                    }}
                >
                    <Box
                        component="img"
                        sx={{ width: 80 }}
                        src={staticRoot + 'web/img/oakra-white.png'}
                        alt="Oakra logo"
                    />
                </Link>
                <Box sx={{ display: 'flex' }}>
                    <LanguageButton />
                    {loggedIn && (
                        <>
                            <IconButton
                                color="inherit"
                                aria-owns={open ? 'menu-appbar' : undefined}
                                aria-haspopup="true"
                                onClick={(e) => setAppBarMenuEl(e.currentTarget)}
                            >
                                <AccountCircle />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                open={open}
                                anchorEl={appBarMenuEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                onClose={() => setAppBarMenuEl(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setAppBarMenuEl(null)
                                        navigate(getPath('settingsStores'))
                                    }}
                                >
                                    <ListItemIcon>
                                        <SettingsIcon />
                                    </ListItemIcon>
                                    <ListItemText>{gettext('Settings')}</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => mutate()}>
                                    <ListItemIcon>
                                        <ExitToAppIcon />
                                    </ListItemIcon>
                                    <ListItemText>{gettext('Logout')}</ListItemText>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>
            </Toolbar>
        </MuiAppBar>
    )
}
